import { ROUTES } from '@constants/routes';
import { specDisplayName } from './format';

// Finding a spec in the backfill picker's list by text. The list is the whole catalogue — the
// backfill specs route returns every spec at once, with no search parameter — so everything here
// works in memory over what the page already holds, and nothing asks the server.
//
// Two callers share it: the picker's typeahead, narrowing the list as the analyst types, and the
// page's ?spec= parameter, which another screen uses to hand an analyst here with the spec already
// chosen. One matcher for both means a link that names a spec finds exactly what typing the same
// text into the picker would show.

// Every whitespace-separated token of the query has to appear somewhere in the spec's display
// name, its id, or its description, in any order and ignoring case. "buyback v1" finds
// forward_buyback_intent_v1 whether the analyst remembers the name or the id; "dividend" also
// finds a spec whose description mentions dividends under a name that does not. An empty query
// matches nothing — the typeahead shows results only once there is something to narrow by, the
// way the stock search bar does.
export const matchSpecs = (specs, query) => {
  const tokens = String(query || '')
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  if (tokens.length === 0) return [];

  return (specs || []).filter((spec) => {
    const haystack = [specDisplayName(spec), spec.score_spec_id, spec.description]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return tokens.every((token) => haystack.includes(token));
  });
};

// ?spec=<id or search text> on the backfill page pre-selects a spec once the list has loaded.
export const SPEC_PARAM = 'spec';

// Takes a query string (with or without the '?'). Null when the parameter is absent or blank.
export const readSpecParam = (search) => {
  const raw = new URLSearchParams(search || '').get(SPEC_PARAM);
  const trimmed = raw ? raw.trim() : '';
  return trimmed || null;
};

// The id to select for a ?spec= value, or null to select nothing.
//
// An exact score_spec_id wins outright: that is what a link built by scoreBackfillPathForSpec
// carries. Anything else is run through the picker's own matcher, and taken ONLY when it names a
// single spec. A backfill is a paid run, so a value that fits several specs ("v1", "forward")
// selects none of them rather than guessing — the analyst lands on the picker and chooses.
export const resolveSpecParam = (specs, raw) => {
  const value = raw ? String(raw).trim() : '';
  if (!value || !specs || specs.length === 0) return null;

  const exact = specs.find((spec) => spec.score_spec_id === value);
  if (exact) return exact.score_spec_id;

  const matches = matchSpecs(specs, value);
  return matches.length === 1 ? matches[0].score_spec_id : null;
};

// The backfill page with a spec pre-selected — what another screen's "Run a backfill" points at.
export const scoreBackfillPathForSpec = (specId) => {
  if (!specId) return ROUTES.scoreBackfill;
  const params = new URLSearchParams();
  params.set(SPEC_PARAM, specId);
  return `${ROUTES.scoreBackfill}?${params.toString()}`;
};
